import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Linkedin, Twitter, Youtube, Facebook, Instagram, Save, RotateCcw, ExternalLink,
  Building2, BarChart3, FileText, BarChart, Activity, Images,
} from "lucide-react";
import { api } from "../../lib/api";
import ImageUpload from "../../components/admin/ImageUpload";

const TABS = [
  { id: "branding", label: "Branding", icon: Building2 },
  { id: "hero", label: "Hero Carousel", icon: Images },
  { id: "about", label: "About & Stats", icon: BarChart3 },
  { id: "footer", label: "Footer", icon: FileText },
  { id: "social", label: "Social Media", icon: Linkedin },
  { id: "analytics", label: "Analytics", icon: Activity },
];

const TEXT = (k, label, placeholder = "") => ({ key: k, kind: "text", label, placeholder });
const AREA = (k, label, placeholder = "") => ({ key: k, kind: "textarea", label, placeholder });
const NUM  = (k, label, hint = "") => ({ key: k, kind: "number", label, hint });
const LINK = (k, label, icon, placeholder = "") => ({ key: k, kind: "url", label, icon, placeholder });
const IMG  = (k, label, hint = "") => ({ key: k, kind: "image", label, hint });

const SCHEMA = {
  branding: [
    IMG("gesil_logo_url", "GESIL Logo", "Upload a PNG/SVG (recommended: transparent background, 400×400 px). Or paste an external URL below."),
    TEXT("company_name", "Company Name", "Global e-Village Services India Ltd"),
  ],
  hero: [
    NUM("hero_autoplay_ms", "Auto-rotate interval (milliseconds)", "Time before the hero carousel advances to the next slide. 1000 ms = 1 second. Set to 0 to disable auto-rotate. Range: 0 – 60000."),
  ],
  about: [
    TEXT("about_subtitle", "Eyebrow", "About GESIL"),
    TEXT("about_title", "Title", "Driving Digital Governance Across India."),
    AREA("about_description", "Description", "Long description shown under the title…"),
    TEXT("stat_1_label", "Stat 1 — Label"),
    NUM("stat_1_value", "Stat 1 — Value"),
    TEXT("stat_1_suffix", "Stat 1 — Suffix"),
    TEXT("stat_2_label", "Stat 2 — Label"),
    NUM("stat_2_value", "Stat 2 — Value"),
    TEXT("stat_2_suffix", "Stat 2 — Suffix"),
    TEXT("stat_3_label", "Stat 3 — Label"),
    NUM("stat_3_value", "Stat 3 — Value"),
    TEXT("stat_3_suffix", "Stat 3 — Suffix"),
    TEXT("stat_4_label", "Stat 4 — Label"),
    NUM("stat_4_value", "Stat 4 — Value"),
    TEXT("stat_4_suffix", "Stat 4 — Suffix"),
  ],
  footer: [
    AREA("footer_about_text", "Footer About-GESIL text", "Tagline + short company description shown in the footer left column."),
  ],
  social: [
    LINK("linkedin_url", "LinkedIn URL", Linkedin),
    LINK("twitter_url", "Twitter / X URL", Twitter),
    LINK("youtube_url", "YouTube URL", Youtube),
    LINK("facebook_url", "Facebook URL", Facebook),
    LINK("instagram_url", "Instagram URL", Instagram),
  ],
  analytics: [
    TEXT("ga_measurement_id", "Google Analytics 4 Measurement ID", "G-XXXXXXXXXX"),
  ],
};

const isValidUrl = (v) => {
  if (!v) return true;
  // Allow backend-relative object-storage paths like /api/files/logo/xyz.png
  if (v.startsWith("/api/") || v.startsWith("/")) return true;
  try {
    const u = new URL(v);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

export default function AdminSettings() {
  const [tab, setTab] = useState("branding");
  const [data, setData] = useState({});
  const [original, setOriginal] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/site-settings`)
      .then((res) => { setData(res.data); setOriginal(res.data); })
      .catch(() => toast.error("Failed to load site settings."))
      .finally(() => setLoading(false));
  }, []);

  const dirty = Object.keys(data).some((k) => data[k] !== original[k]);

  const save = async () => {
    for (const f of SCHEMA.social) {
      if (!isValidUrl(data[f.key])) {
        toast.error(`${f.label}: please enter a valid http(s) URL.`);
        return;
      }
    }
    if (data.gesil_logo_url && !isValidUrl(data.gesil_logo_url)) {
      toast.error("Logo: please upload an image or paste a valid http(s) URL.");
      return;
    }
    setSaving(true);
    try {
      const res = await api.put(`/site-settings`, data);
      setData(res.data); setOriginal(res.data);
      toast.success("Settings saved. Site updated.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save.");
    } finally { setSaving(false); }
  };

  const reset = () => setData(original);

  if (loading) return <div className="text-[#475569]">Loading settings…</div>;

  const fields = SCHEMA[tab] || [];

  return (
    <div data-testid="admin-settings" className="max-w-4xl">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#2563EB]">Site Settings</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A] mt-2">Site Content & Configuration</h1>
        <p className="text-[#475569] mt-2 text-sm">Edit headline content, statistics, social URLs, branding, footer and Google Analytics. Changes apply on next page load.</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-blue-100 mb-6">
        {TABS.map((t) => {
          const Ic = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider border-b-2 transition-colors ${
                active
                  ? "border-[#2563EB] text-[#1D4ED8]"
                  : "border-transparent text-[#475569] hover:text-[#2563EB]"
              }`}
            >
              <Ic size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="space-y-4">
        {fields.map((f) => (
          <Field key={f.key} field={f} value={data[f.key] ?? ""} onChange={(v) => setData((d) => ({ ...d, [f.key]: v }))} />
        ))}
      </div>

      <div className="mt-8 flex items-center gap-3 sticky bottom-4 bg-white/85 backdrop-blur p-4 rounded-2xl border border-blue-100 shadow-[0_12px_40px_-18px_rgba(37,99,235,0.35)]">
        <button onClick={save} disabled={!dirty || saving} data-testid="save-settings"
          className="inline-flex items-center gap-2 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-50 text-white px-5 py-2.5 font-semibold text-sm">
          <Save size={14} /> {saving ? "Saving…" : "Save changes"}
        </button>
        <button onClick={reset} disabled={!dirty || saving} data-testid="reset-settings"
          className="inline-flex items-center gap-2 rounded-lg border border-blue-200 hover:border-[#2563EB] hover:text-[#2563EB] disabled:opacity-50 text-[#0F172A] px-4 py-2.5 font-semibold text-sm">
          <RotateCcw size={14} /> Reset
        </button>
        {dirty && <span className="text-xs text-amber-600 font-semibold">● Unsaved changes</span>}
      </div>
    </div>
  );
}

function Field({ field, value, onChange }) {
  const Icon = field.icon || BarChart;
  if (field.kind === "textarea") {
    return (
      <div className="bg-white border border-blue-100 rounded-2xl p-5">
        <label className="text-sm font-semibold text-[#0F172A]">{field.label}</label>
        <textarea
          value={value || ""} onChange={(e) => onChange(e.target.value)}
          rows={4} placeholder={field.placeholder}
          data-testid={`input-${field.key}`}
          className="mt-2 w-full px-4 py-2.5 rounded-lg border border-blue-200 focus:border-[#2563EB] focus:ring-4 focus:ring-blue-100 outline-none text-sm resize-y"
        />
      </div>
    );
  }
  if (field.kind === "number") {
    return (
      <div className="bg-white border border-blue-100 rounded-2xl p-5">
        <label className="text-sm font-semibold text-[#0F172A]">{field.label}</label>
        <input
          type="number" value={value ?? 0} onChange={(e) => onChange(parseInt(e.target.value || "0", 10))}
          data-testid={`input-${field.key}`}
          className="mt-2 w-40 px-4 py-2.5 rounded-lg border border-blue-200 focus:border-[#2563EB] focus:ring-4 focus:ring-blue-100 outline-none text-sm"
        />
        {field.hint && <p className="mt-2 text-xs text-[#64748B] leading-relaxed">{field.hint}</p>}
      </div>
    );
  }
  if (field.kind === "image") {
    return (
      <div className="bg-white border border-blue-100 rounded-2xl p-5">
        <label className="text-sm font-semibold text-[#0F172A]">{field.label}</label>
        {field.hint && <p className="mt-1 text-xs text-[#64748B] leading-relaxed">{field.hint}</p>}
        <div className="mt-3">
          <ImageUpload value={value || ""} onChange={onChange} label={field.label} testid={`img-${field.key}`} />
        </div>
        <div className="mt-3">
          <label className="text-[11px] uppercase tracking-wider font-semibold text-[#475569]">Or paste image URL</label>
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://… or /api/files/logo/…"
            data-testid={`input-${field.key}`}
            className="mt-1 w-full px-4 py-2.5 rounded-lg border border-blue-200 focus:border-[#2563EB] focus:ring-4 focus:ring-blue-100 outline-none text-sm"
          />
        </div>
      </div>
    );
  }
  const showOpen = field.kind === "url" && value && isValidUrl(value);
  return (
    <div className="bg-white border border-blue-100 rounded-2xl p-5">
      <label className="flex items-center gap-2 text-sm font-semibold text-[#0F172A]">
        {field.icon && <span className="h-8 w-8 rounded-lg bg-blue-50 text-[#2563EB] inline-flex items-center justify-center"><Icon size={15} /></span>}
        {field.label}
      </label>
      <div className="mt-2 flex gap-2">
        <input
          type={field.kind === "url" ? "url" : "text"}
          value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder} data-testid={`input-${field.key}`}
          className="flex-1 px-4 py-2.5 rounded-lg border border-blue-200 focus:border-[#2563EB] focus:ring-4 focus:ring-blue-100 outline-none text-sm"
        />
        {showOpen && (
          <a href={value} target="_blank" rel="noopener noreferrer" title="Open" className="px-3 inline-flex items-center justify-center rounded-lg border border-blue-200 hover:border-[#2563EB] text-[#475569]">
            <ExternalLink size={14} />
          </a>
        )}
      </div>
    </div>
  );
}
